import { getNookClient, NookApiError } from "@nook/api-client"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { safeRedirect } from "@/components/auth-page.js"
import { supabase } from "@/lib/supabase.js"

const str = (value: unknown) => (typeof value === "string" ? value : undefined)

type AuthSearch = Partial<
  Record<"redirect" | "error_code" | "error_description", string | undefined>
>

/**
 * The signed-out layout: `/signin` and `/signup` sit in `_auth/`, as protected
 * screens sit in `_authed/`, so a new auth surface inherits this guard by landing here.
 *
 * The inverse of `_authed`'s guard. Without it, signing out and pressing Back
 * — or bookmarking this URL — shows a signed-in user a sign-in button, which
 * reads as a failure even though nothing failed. (Block 02.)
 *
 * A soft-deleted account still gets a Google session; only the API knows to
 * refuse it (block 34, A9 C7). So ask before redirecting, and sign it out.
 * Redirect to the message first and sign out there: `signOut` makes `main.tsx`
 * invalidate the router, which reloads the current URL and would drop a
 * redirect thrown after it.
 */
export const Route = createFileRoute("/_auth")({
  validateSearch: (search): AuthSearch => ({
    redirect: str(search.redirect),
    error_code: str(search.error_code),
    error_description: str(search.error_description),
  }),
  beforeLoad: async ({ search, location }) => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) return

    const deleted = await getNookClient()
      .getMe()
      .then(
        () => false,
        (error) => error instanceof NookApiError && error.code === "account_deleted",
      )
    if (!deleted) throw redirect({ href: safeRedirect(search.redirect) })

    if (search.error_code !== "account_deleted")
      throw redirect({
        to: location.pathname,
        search: { ...search, error_code: "account_deleted" },
      })
    await supabase.auth.signOut()
  },
})
